import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

// Lazy load all page components for code splitting
const Index = lazy(() => import("./pages/Index"));
const NotFound = lazy(() => import("./pages/NotFound"));
const LocationHub = lazy(() => import("./pages/LocationHub"));
const NeighborhoodPage = lazy(() => import("./pages/NeighborhoodPage"));
const ServiceLocationPage = lazy(() => import("./pages/ServiceLocationPage"));
const ServicePage = lazy(() => import("./pages/ServicePage"));
const EdmontonPolyBReplacementPage = lazy(() => import("./pages/EdmontonPolyBReplacementPage"));
const SherwoodParkPolyBReplacementPage = lazy(() => import("./pages/SherwoodParkPolyBReplacementPage"));
const EdmontonHotWaterTankPage = lazy(() => import("./pages/EdmontonHotWaterTankPage"));
const SherwoodParkHotWaterTankPage = lazy(() => import("./pages/SherwoodParkHotWaterTankPage"));
const EdmontonEmergencyPlumberPage = lazy(() => import("./pages/EdmontonEmergencyPlumberPage"));
const SherwoodParkEmergencyPlumberPage = lazy(() => import("./pages/SherwoodParkEmergencyPlumberPage"));
const EdmontonFurnaceRepairPage = lazy(() => import("./pages/EdmontonFurnaceRepairPage"));
const SherwoodParkFurnaceRepairPage = lazy(() => import("./pages/SherwoodParkFurnaceRepairPage"));
const EdmontonGasFittingPage = lazy(() => import("./pages/EdmontonGasFittingPage"));
const SherwoodParkGasFittingPage = lazy(() => import("./pages/SherwoodParkGasFittingPage"));
const EdmontonSumpPumpPage = lazy(() => import("./pages/EdmontonSumpPumpPage"));
const SherwoodParkSumpPumpPage = lazy(() => import("./pages/SherwoodParkSumpPumpPage"));

const queryClient = new QueryClient();

// Minimal loading fallback that doesn't affect layout
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-pulse text-muted-foreground">Loading...</div>
  </div>
);

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              
              {/* Enhanced SEO Service Pages - Local Mirroring Architecture */}
              <Route path="/edmonton/poly-b-replacement" element={<EdmontonPolyBReplacementPage />} />
              <Route path="/sherwood-park/poly-b-replacement" element={<SherwoodParkPolyBReplacementPage />} />
              <Route path="/edmonton/hot-water-tank-replacement" element={<EdmontonHotWaterTankPage />} />
              <Route path="/sherwood-park/hot-water-tank-replacement" element={<SherwoodParkHotWaterTankPage />} />
              <Route path="/edmonton/emergency-plumber" element={<EdmontonEmergencyPlumberPage />} />
              <Route path="/sherwood-park/emergency-plumber" element={<SherwoodParkEmergencyPlumberPage />} />
              <Route path="/edmonton/furnace-repair" element={<EdmontonFurnaceRepairPage />} />
              <Route path="/sherwood-park/furnace-repair" element={<SherwoodParkFurnaceRepairPage />} />
              <Route path="/edmonton/gas-fitting" element={<EdmontonGasFittingPage />} />
              <Route path="/sherwood-park/gas-fitting" element={<SherwoodParkGasFittingPage />} />
              <Route path="/edmonton/sump-pump-installation" element={<EdmontonSumpPumpPage />} />
              <Route path="/sherwood-park/sump-pump-installation" element={<SherwoodParkSumpPumpPage />} />
              
              {/* SEO Service Pages - Batch 1 (Pages 1-16) */}
              <Route path="/poly-b-replacement-edmonton" element={<ServicePage />} />
              <Route path="/water-line-replacement" element={<ServicePage />} />
              <Route path="/sump-pump-installation-sherwood-park" element={<ServicePage />} />
              <Route path="/commercial-plumbing-maintenance" element={<ServicePage />} />
              <Route path="/water-softener-installation-repair" element={<ServicePage />} />
              <Route path="/poly-b-replacement-sherwood-park" element={<ServicePage />} />
              <Route path="/edmonton-drain-cleaning" element={<ServicePage />} />
              <Route path="/sherwood-park-drain-cleaning" element={<ServicePage />} />
              <Route path="/reverse-osmosis-drinking-water-systems" element={<ServicePage />} />
              <Route path="/emergency-furnace-repair" element={<ServicePage />} />
              <Route path="/edmonton-furnace-repair" element={<ServicePage />} />
              <Route path="/furnace-repair-sherwood-park" element={<ServicePage />} />
              <Route path="/gas-line-installation-repair" element={<ServicePage />} />
              <Route path="/sump-pump-repair-installation-sherwood-park" element={<ServicePage />} />
              <Route path="/kitchen-plumbing-services" element={<ServicePage />} />
              <Route path="/tankless-water-heater-installation" element={<ServicePage />} />
              
              {/* SEO Service Pages - Batch 2 (Pages 17-32) */}
              <Route path="/toilet-repair-installation" element={<ServicePage />} />
              <Route path="/water-heater-repair-installation" element={<ServicePage />} />
              <Route path="/faucet-sink-repair" element={<ServicePage />} />
              <Route path="/emergency-plumbing-sherwood-park" element={<ServicePage />} />
              <Route path="/pipe-leak-detection" element={<ServicePage />} />
              <Route path="/garage-heater-installation" element={<ServicePage />} />
              <Route path="/boiler-installation" element={<ServicePage />} />
              <Route path="/high-efficiency-furnace-installation" element={<ServicePage />} />
              <Route path="/gas-fitting-services-edmonton" element={<ServicePage />} />
              <Route path="/gas-fitting-services-sherwood-park" element={<ServicePage />} />
              <Route path="/bathroom-plumbing-renovation" element={<ServicePage />} />
              <Route path="/water-softener-installation-sherwood-park" element={<ServicePage />} />
              <Route path="/water-softener-installation-edmonton" element={<ServicePage />} />
              <Route path="/emergency-plumber-edmonton" element={<ServicePage />} />
              <Route path="/boiler-repair-maintenance" element={<ServicePage />} />
              <Route path="/hot-water-tank-replacement" element={<ServicePage />} />
              
              {/* pSEO Location Pages - Hidden from main nav */}
              <Route path="/locations" element={<LocationHub />} />
              <Route path="/locations/:city/:neighborhood" element={<NeighborhoodPage />} />
              <Route path="/locations/:city/:neighborhood/:service" element={<ServiceLocationPage />} />
              
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
